/**
 * Owns the transaction for creating or opening an AI Chat session.
 * A newer selection invalidates delayed responses from an older selection.
 */
export function createSessionOpeningController({
  sessionFacade,
  sessionStore,
  messageStore,
  artifactFacade,
  runFacade,
  canvasStore,
  setActiveSession,
  requestStreamingSnapshot,
  loadInstalledSkills,
  renderToolsAlwaysOn,
  setCurrentRoleCode,
  hideRoleCards,
  showSessionLoading,
  updateHeader,
  renderSessionList,
  renderMessages,
  updateInputVisibility,
  updateTokenCounter,
  resetScrollState,
  closeMobileSidebar,
  renderSessionLoadFailure,
  showToast,
  friendlyAgentError,
} = {}) {
  let openVersion = 0;

  const isCurrent = (version) => version === openVersion;

  async function hydrateSession(id, version) {
    const result = await sessionFacade.openSession(id);
    if (!isCurrent(version)) return null;
    sessionFacade.applySessionContext(result.context);
    await Promise.all([artifactFacade.loadArtifacts(id), runFacade.loadRuns(id)]);
    if (!isCurrent(version)) return null;

    canvasStore.retainSessionArtifacts(id);
    messageStore.setMessages(result.messages || []);
    requestStreamingSnapshot(id);
    setCurrentRoleCode(result.session?.roleCode);
    await loadInstalledSkills(result.session?.roleCode);
    if (!isCurrent(version)) return null;

    renderToolsAlwaysOn();
    renderMessages();
    sessionStore.setCurrentSession(result.session);
    sessionStore.upsertSession(result.session);
    updateHeader(result.session);
    renderSessionList();
    hideRoleCards();
    updateInputVisibility(true);
    updateTokenCounter();
    resetScrollState();
    closeMobileSidebar();
    return result;
  }

  async function openExistingSession(id) {
    const version = ++openVersion;
    setActiveSession(id);
    showSessionLoading();
    try {
      return await hydrateSession(id, version);
    } catch (error) {
      if (!isCurrent(version)) return null;
      showToast(friendlyAgentError(error), 'error');
      renderSessionLoadFailure();
      return null;
    }
  }

  async function createSessionWithRole(code, { roleCards = [], newSessionButton = null } = {}) {
    roleCards.forEach((card) => {
      card.classList.add('aichat-role-card--loading');
      card.setAttribute('aria-busy', 'true');
    });
    if (newSessionButton) newSessionButton.disabled = true;
    const version = ++openVersion;
    try {
      const session = await sessionFacade.createSession({ roleCode: code });
      if (!isCurrent(version)) return null;
      setCurrentRoleCode(code);
      setActiveSession(session.id);
      hideRoleCards();
      showSessionLoading();
      return await hydrateSession(session.id, version);
    } catch (error) {
      if (!isCurrent(version)) return null;
      showToast(friendlyAgentError(error) || '创建会话失败', 'error');
      return null;
    } finally {
      roleCards.forEach((card) => {
        card.classList.remove('aichat-role-card--loading');
        card.removeAttribute('aria-busy');
      });
      if (newSessionButton) newSessionButton.disabled = false;
    }
  }

  return Object.freeze({
    createSessionWithRole,
    openExistingSession,
    invalidatePendingOpen: () => { openVersion += 1; },
  });
}
